// Imports for external libraries go here.
import React, { FC, useMemo } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in the list.
import { SkeletonLoaderProps } from './SkeletonLoader.types';
import { StyledSkeletonLoader } from './SkeletonLoader.styles';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';

const getListViewSkeletonLayout = (viewPort?: string): { sectionCount: number; cardsPerSection: number } => {
  if (viewPort === 'Mobile' || viewPort === 'Small Mobile') {
    return { sectionCount: 1, cardsPerSection: 4 };
  }
  if (viewPort === 'viewPortM') {
    return { sectionCount: 2, cardsPerSection: 4 };
  }
  return { sectionCount: 1, cardsPerSection: 9 };
};

const getPrimaryTitleSkeletonWidth = (viewPort?: string): number => {
  if (viewPort === 'Mobile' || viewPort === 'Small Mobile') {
    return 231;
  }
  if (viewPort === 'viewPortM') {
    return 239;
  }
  return 250;
};

const ListCardSkeleton: FC<{ titleSkeletonWidth: number }> = ({ titleSkeletonWidth }) => (
  <div className="list-card-skeleton-card">
    <div className="loader-container d-flex">
      <div className={clsx('image-place-loader')} aria-hidden>
        <Skeleton height={96} width={96} borderRadius={4} />
      </div>
      <div className="d-flex flex-column">
        <Skeleton className={clsx('skeleton-el')} height={40} width={titleSkeletonWidth} />
        <Skeleton className={clsx('skeleton-el mt-2')} height={19} width={125} />
        <Skeleton className={clsx('skeleton-el mt-2')} height={16} width={100} />
      </div>
    </div>
    <hr className="list-card-skeleton-divider" />
  </div>
);

// Use named rather than default exports.
export const SkeletonLoader: FC<SkeletonLoaderProps> = ({ viewPort }) => {
  const { sectionCount, cardsPerSection } = useMemo(() => getListViewSkeletonLayout(viewPort), [viewPort]);
  const titleSkeletonWidth = useMemo(() => getPrimaryTitleSkeletonWidth(viewPort), [viewPort]);

  return (
    <StyledSkeletonLoader data-testid="luxury-SkeletonLoader" data-component-name="m-luxury-SkeletonLoader">
      <div data-testid="list-view-spinner" role="status" aria-live="polite" aria-label="Loading results">
        {Array.from({ length: sectionCount }, (_, sectionIndex) => (
          <div className="d-block mb-2 mb-md-0 paginated-group" key={sectionIndex}>
            <Skeleton className={clsx('skeleton-el', 'mb-3')} height={24} width={146} />
            <div className="d-flex flex-wrap list-view-skeleton-card-row">
              {Array.from({ length: cardsPerSection }, (_, cardIndex) => (
                <div className="col-xl-4 col-md-6 col-12 px-0" key={`${sectionIndex}-${cardIndex}`}>
                  <ListCardSkeleton titleSkeletonWidth={titleSkeletonWidth} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </StyledSkeletonLoader>
  );
};
